import React from 'react';
import { Loader2, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';

// iter494b — Small helper: group a flat list of site rows by
// `client_name` while preserving the parent's ordering (first
// appearance wins). Used by every bulk dialog so overrides / uplifts
// are visually clustered under their client heading.
function groupByClient(sites) {
  const order = [];
  const buckets = new Map();
  for (const s of (sites || [])) {
    const key = s.client_name || 'Unassigned';
    if (!buckets.has(key)) { buckets.set(key, []); order.push(key); }
    buckets.get(key).push(s);
  }
  return order.map(k => ({ client_name: k, sites: buckets.get(k) }));
}

// ── ReviewSection ─────────────────────────────────────────────────
// Collapsible + toggleable section within the Review & Publish dialog.
// A section can be skipped from this publish by unchecking `include`.
function ReviewSection({ title, count, tone = 'slate', include, onToggle, defaultOpen = true, children, testid }) {
  const [open, setOpen] = React.useState(defaultOpen);
  const bg = tone === 'emerald' ? 'bg-emerald-50/60 border-emerald-200'
    : tone === 'amber' ? 'bg-amber-50/50 border-amber-200'
      : 'bg-slate-50 border-slate-200';
  return (
    <div className={`rounded-lg border ${bg}`} data-testid={testid}>
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="flex items-center gap-1 text-slate-700 hover:text-slate-900"
          data-testid={`${testid}-toggle`}
        >
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        </button>
        <label className="flex items-center gap-2 flex-1 cursor-pointer">
          <Checkbox
            checked={include}
            onCheckedChange={(v) => onToggle(!!v)}
            disabled={count === 0}
            data-testid={`${testid}-include`}
          />
          <span className="text-xs font-semibold text-slate-800">
            {title}
            <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] ${count === 0 ? 'bg-slate-200 text-slate-500' : 'bg-slate-900 text-white'}`}>
              {count}
            </span>
          </span>
        </label>
      </div>
      {open && count > 0 && (
        <div className="px-3 pb-3">{children}</div>
      )}
      {open && count === 0 && (
        <div className="px-3 pb-3 text-[11px] italic text-slate-400">Nothing pending in this bucket.</div>
      )}
    </div>
  );
}

// ── BulkActionDialog / Review & Publish (iter495) ─────────────────
// Unified "review then publish" flow. Replaces the old Save-All /
// Master-All / Save-&-Master split. Users get one dialog with three
// clearly-labelled sections (Section 2 changes, Overrides, Uplifts),
// each of which can be individually included or excluded from the
// push before hitting one single Publish button.
export function BulkActionDialog({ overrideSites, upliftPromotionSites = [], monthOverrideSites = [], pendingDiscontinueIds, pendingDiscontinueSiteIds, allSection2Sites = [], section3Rows = [], clients, dateRange, onCancel, onConfirm }) {
  const [busy, setBusy] = React.useState(false);

  const discontinueClients = React.useMemo(() => {
    const ids = Array.from(pendingDiscontinueIds || []);
    return ids.map(id => clients.find(c => c.id === id) || { id, name: `Client #${id}` });
  }, [pendingDiscontinueIds, clients]);

  const discontinueSites = React.useMemo(() => {
    const ids = Array.from(pendingDiscontinueSiteIds || []);
    return ids.map(id => allSection2Sites.find(s => s.site_id === id) || { site_id: id, site_name: `Site #${id}` });
  }, [pendingDiscontinueSiteIds, allSection2Sites]);

  const overrideCount = overrideSites.length;
  // iter_fix_budget_v2 — Month-override sites are no longer silently skipped by
  // the backend. Their explicit £/job price IS pushed; monthly figures are
  // preserved. Include them in publishableOverrideCount.
  const publishableOverrideCount = overrideCount;
  const monthOverrideInfoSites = monthOverrideSites || [];
  const upliftCount = upliftPromotionSites.length;
  const discontinueCount = discontinueClients.length + discontinueSites.length;
  const section3Count = section3Rows.length;
  const section3Total = React.useMemo(
    () => section3Rows.reduce((sum, r) => sum + Number(r.total || 0), 0),
    [section3Rows]
  );

  // Per-section include flags. Default: everything with a non-zero
  // count is included; sections with nothing to do stay unchecked.
  const [includeSaves, setIncludeSaves] = React.useState(discontinueCount > 0);
  const [includeOverrides, setIncludeOverrides] = React.useState(publishableOverrideCount > 0);
  const [includeUplifts, setIncludeUplifts] = React.useState(upliftCount > 0);

  const totalIncluded = (includeSaves ? discontinueCount : 0)
    + (includeOverrides ? publishableOverrideCount : 0)
    + (includeUplifts ? upliftCount : 0);

  const run = async () => {
    setBusy(true);
    try {
      await onConfirm({
        includeSaves,
        includeOverrides,
        includeUplifts,
      });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onCancel(); }}>
      <DialogContent
        data-testid="bulk-action-dialog"
        className="max-w-[95vw] w-[95vw] max-h-[85vh] flex flex-col overflow-hidden"
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Review &amp; Publish</DialogTitle>
          <p className="text-xs text-slate-500 pt-0.5">
            One flow for everything pending on this budget. Tick the sections you want to publish; skip any you&apos;re not ready for.
          </p>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-3">
          {/* Summary strip */}
          <div className="flex items-center gap-3 flex-wrap text-[11px] px-1">
            <span className="text-slate-500">Pending:</span>
            <span><b>{discontinueCount}</b> Section 2 changes</span>
            <span className="text-slate-300">·</span>
            <span><b>{publishableOverrideCount}</b> overrides</span>
            {monthOverrideInfoSites.length > 0 && (
              <span className="text-blue-600" title="Sites with manual per-month £ — their explicit price will still be pushed; monthly figures are preserved">
                (<b>{monthOverrideInfoSites.length}</b> with monthly pricing — see below)
              </span>
            )}
            <span className="text-slate-300">·</span>
            <span><b>{upliftCount}</b> uplifted prices</span>
            <span className="text-slate-300">·</span>
            <span><b>{section3Count}</b> new business</span>
          </div>

          {monthOverrideInfoSites.length > 0 && (
            <div
              className="border border-blue-200 bg-blue-50 rounded-md p-3 text-xs text-blue-900"
              data-testid="review-month-override-info"
            >
              <div className="font-semibold uppercase tracking-tight text-[11px] mb-1 flex items-center gap-1.5">
                <span>ℹ</span>
                <span>{monthOverrideInfoSites.length} site{monthOverrideInfoSites.length === 1 ? '' : 's'} with manual per-month pricing</span>
              </div>
              <p className="text-[11px] text-blue-800 mb-2">
                These sites have calendarised monthly £ overrides for revenue planning. Their explicit reviewed £/job price <strong>will still be pushed</strong> to Customer Master. Monthly budget figures are preserved and unaffected.
              </p>
              <div className="max-h-32 overflow-y-auto border border-blue-200 rounded bg-white">
                <table className="w-full text-[11px]">
                  <thead className="bg-blue-100 text-blue-900">
                    <tr>
                      <th className="text-left px-2 py-1">Client</th>
                      <th className="text-left px-2 py-1">Site</th>
                      <th className="text-right px-2 py-1">Monthly overrides</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthOverrideInfoSites.map(s => (
                      <tr key={s.site_id} className="border-t border-blue-100" data-testid={`review-month-info-${s.site_id}`}>
                        <td className="px-2 py-1">{s.client_name}</td>
                        <td className="px-2 py-1">{s.site_name}</td>
                        <td className="px-2 py-1 text-right">{Object.keys(s.month_overrides || {}).length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* 1. Section 2 discontinues */}
          <ReviewSection
            title="1. Section 2 changes"
            count={discontinueCount}
            include={includeSaves}
            onToggle={setIncludeSaves}
            testid="review-section-saves"
          >
            {discontinueClients.length > 0 && (
              <div className="mb-2">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Discontinue clients</div>
                <div className="border rounded-md overflow-hidden bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 text-slate-600">
                      <tr><th className="text-left px-2 py-1.5">Client</th></tr>
                    </thead>
                    <tbody>
                      {discontinueClients.map(c => (
                        <tr key={c.id} className="border-t" data-testid={`bulk-discontinue-row-${c.id}`}>
                          <td className="px-2 py-1.5">{c.name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {discontinueSites.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Discontinue sites</div>
                <div className="border rounded-md overflow-hidden bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 text-slate-600">
                      <tr><th className="text-left px-2 py-1.5">Site</th></tr>
                    </thead>
                    <tbody>
                      {discontinueSites.map(s => (
                        <tr key={s.site_id} className="border-t" data-testid={`bulk-discontinue-site-row-${s.site_id}`}>
                          <td className="px-2 py-1.5">{s.site_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </ReviewSection>

          {/* 2. Overrides → master */}
          <ReviewSection
            title="2. Overrides → customer master"
            count={publishableOverrideCount}
            include={includeOverrides}
            onToggle={setIncludeOverrides}
            tone="amber"
            testid="review-section-overrides"
          >
            <div className="border rounded-md overflow-hidden bg-white">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-slate-600">
                  <tr>
                    <th className="text-left px-2 py-1.5">Site</th>
                    <th className="text-left px-2 py-1.5">Current master</th>
                    <th className="text-left px-2 py-1.5">→ New (this push)</th>
                    <th className="text-left px-2 py-1.5">Effective from</th>
                  </tr>
                </thead>
                <tbody>
                  {groupByClient(overrideSites).map(({ client_name, sites }) => (
                    <React.Fragment key={`ov-${client_name}`}>
                      <tr className="bg-amber-50/60 border-t">
                        <td colSpan={4} className="px-2 py-1 font-semibold uppercase text-[10px] tracking-wide text-slate-600">
                          {client_name} <span className="text-slate-400 font-normal normal-case">· {sites.length} site{sites.length === 1 ? '' : 's'}</span>
                        </td>
                      </tr>
                      {sites.map(s => {
                        const curParts = [];
                        curParts.push(`£${Number(s.forecast_price || 0).toFixed(2)}`);
                        if (s.frequency) {
                          curParts.push(`${s.frequency}${s.custom_frequency_days ? ` (${s.custom_frequency_days}d)` : ''}`);
                        }
                        if (s.primary_service_name) curParts.push(s.primary_service_name);
                        const newParts = [];
                        if (s.price_override != null) newParts.push(`£${Number(s.price_override).toFixed(2)}`);
                        if (s.frequency_override) newParts.push(`${s.frequency_override}${s.custom_frequency_days_override ? ` (${s.custom_frequency_days_override}d)` : ''}`);
                        if (s.service_id_override) newParts.push('Service change');
                        if (s.start_date_override) newParts.push(`Start ${s.start_date_override}`);
                        if (s.notes) newParts.push(`“${s.notes}”`);
                        return (
                          <tr key={s.site_id} className="border-t align-top" data-testid={`bulk-master-row-${s.site_id}`}>
                            <td className="px-2 py-1.5 font-medium pl-6">{s.site_name}</td>
                            <td className="px-2 py-1.5 text-slate-500">{curParts.join(' · ')}</td>
                            <td className="px-2 py-1.5 text-slate-800 font-medium">{newParts.join(' · ') || '—'}</td>
                            <td className="px-2 py-1.5 text-slate-500">{s.start_date_override || dateRange?.start}</td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </ReviewSection>

          {/* 3. Uplifts → master */}
          <ReviewSection
            title="3. Uplifted £/job → customer master"
            count={upliftCount}
            include={includeUplifts}
            onToggle={setIncludeUplifts}
            tone="emerald"
            testid="review-section-uplifts"
          >
            <div className="border rounded-md overflow-hidden bg-white">
              <table className="w-full text-xs">
                <thead className="bg-emerald-50 text-slate-600">
                  <tr>
                    <th className="text-left px-2 py-1.5">Site</th>
                    <th className="text-right px-2 py-1.5">Current £/job</th>
                    <th className="text-right px-2 py-1.5">→ New £/job</th>
                    <th className="text-right px-2 py-1.5">Δ</th>
                    <th className="text-left px-2 py-1.5">Source</th>
                    <th className="text-left px-2 py-1.5">Effective from</th>
                  </tr>
                </thead>
                <tbody>
                  {groupByClient(upliftPromotionSites).map(({ client_name, sites }) => (
                    <React.Fragment key={`up-${client_name}`}>
                      <tr className="bg-emerald-50/60 border-t">
                        <td colSpan={6} className="px-2 py-1 font-semibold uppercase text-[10px] tracking-wide text-slate-600">
                          {client_name} <span className="text-slate-400 font-normal normal-case">· {sites.length} site{sites.length === 1 ? '' : 's'}</span>
                        </td>
                      </tr>
                      {sites.map(s => {
                        const base = Number(s.forecast_price || 0);
                        const nw = Number(s.uplift_target_price || 0);
                        const delta = nw - base;
                        return (
                          <tr key={s.site_id} className="border-t" data-testid={`bulk-uplift-row-${s.site_id}`}>
                            <td className="px-2 py-1.5 font-medium pl-6">{s.site_name}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-slate-500">£{base.toFixed(2)}</td>
                            <td className="px-2 py-1.5 text-right font-mono font-medium">£{Math.round(nw)}</td>
                            <td className={`px-2 py-1.5 text-right text-[11px] ${delta > 0 ? 'text-emerald-700' : delta < 0 ? 'text-rose-700' : 'text-slate-400'}`}>
                              {delta > 0 ? '+' : ''}£{delta.toFixed(2)}
                            </td>
                            <td className="px-2 py-1.5 text-emerald-700 text-[11px]">via Uplift</td>
                            <td className="px-2 py-1.5 text-slate-500">{s.uplift_effective_from || dateRange?.start}</td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </ReviewSection>

          {/* 4. Section 3 — informational: new-business rows already saved */}
          {section3Count > 0 && (
            <div className="rounded-lg border bg-slate-50 border-slate-200" data-testid="review-section-newbiz">
              <div className="flex items-center gap-2 px-3 py-2">
                <span className="text-xs font-semibold text-slate-800">
                  4. New business (Section 3)
                  <span className="ml-2 rounded-full bg-slate-900 text-white px-2 py-0.5 text-[10px]">
                    {section3Count}
                  </span>
                </span>
                <span className="text-[10px] text-slate-500 ml-auto">
                  Auto-saved when added — shown here for visibility. Full-year total £{section3Total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
              <div className="px-3 pb-3">
                <div className="border rounded-md overflow-hidden bg-white">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-100 text-slate-600">
                      <tr>
                        <th className="text-left px-2 py-1.5">Client</th>
                        <th className="text-left px-2 py-1.5">Site</th>
                        <th className="text-left px-2 py-1.5">Service</th>
                        <th className="text-left px-2 py-1.5">Frequency</th>
                        <th className="text-right px-2 py-1.5">£/job</th>
                        <th className="text-left px-2 py-1.5">Start</th>
                        <th className="text-right px-2 py-1.5">Year total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section3Rows.map(r => (
                        <tr key={`s3-${r.id}`} className="border-t" data-testid={`review-newbiz-row-${r.id}`}>
                          <td className="px-2 py-1.5 font-medium">{r.client_name || '—'}</td>
                          <td className="px-2 py-1.5">{r.site_name || '—'}</td>
                          <td className="px-2 py-1.5 text-slate-600">{r.service_name || '—'}</td>
                          <td className="px-2 py-1.5 text-slate-600">
                            {r.frequency || '—'}
                            {r.custom_days ? ` (${r.custom_days}d)` : ''}
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono">£{Number(r.price || 0).toFixed(2)}</td>
                          <td className="px-2 py-1.5 text-slate-600">{r.start_date || '—'}</td>
                          <td className="px-2 py-1.5 text-right font-mono font-medium">£{Number(r.total || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {(totalIncluded === 0 && section3Count === 0) && (
            <div className="text-xs text-slate-500 italic py-4 text-center">Nothing selected. Tick a section to include it in this publish.</div>
          )}
          {(totalIncluded === 0 && section3Count > 0) && (
            <div className="text-xs text-emerald-700 py-3 text-center">Only Section 3 new-business rows are pending — they&apos;ll be stamped to this batch on Publish and cleared from this dialog.</div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 pt-3 border-t bg-background">
          <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="bulk-cancel-btn">Cancel</Button>
          <Button
            onClick={run}
            disabled={busy || (totalIncluded === 0 && section3Count === 0)}
            data-testid="bulk-confirm-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Check className="h-4 w-4 mr-1" />}
            Publish {totalIncluded + section3Count} change{(totalIncluded + section3Count) === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
